import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { assign, createMachine, DoneInvokeEvent } from "xstate";
import { removeRepeatedItemsFromArray } from "@iventis/utilities";
import { getScheduleNode } from "./schedule-api-functions";

export const COPY_MACHINE_ID = "copyMachine";

export type IdAndParentId = { id: string; parentId: string; name: string };

export interface NodeWithExpanded extends Node {
    parentId: null; // we don't need to know what the parent id is which helps distinguish between parents and children in the dialog
    expandedInDialog: boolean;
}

export type CopyVersionsMachineContext = {
    piblingSchedules: NodeWithExpanded[];
    scheduleVersionIdsToCopy: IdAndParentId[];
};

export type SelectVersionToCopyEvent = {
    type: "SELECT_OR_DESELECT_NODE";
    payload: IdAndParentId;
};

export type ExpandOrCollapseEvent = {
    type: "EXPAND_OR_COLLAPSE";
    payload: SelectVersionToCopyEvent["payload"];
};

export type CopyVersionsMachineEvents = SelectVersionToCopyEvent | ExpandOrCollapseEvent;

/*
    GUARDS
*/

const isExpanded = (context: CopyVersionsMachineContext, event: ExpandOrCollapseEvent | SelectVersionToCopyEvent) =>
    context.piblingSchedules.some((s) => s.id === event.payload.id && s.expandedInDialog);

// In the context of this machine, to distinguish between piblings and piblings children, a piblings' parent id is set to null. Hence the below equality check
const isPibling = (_, event: SelectVersionToCopyEvent) => event.payload.parentId == null;

const isSelected = (context: CopyVersionsMachineContext, event: SelectVersionToCopyEvent) => context.scheduleVersionIdsToCopy.some((s) => s.id === event.payload.id);

const isPiblingSelected = (context: CopyVersionsMachineContext, event: SelectVersionToCopyEvent) => context.scheduleVersionIdsToCopy.some((s) => s.id === event.payload.parentId);

const isPreviouslyFetched = (context: CopyVersionsMachineContext, event: ExpandOrCollapseEvent | SelectVersionToCopyEvent) =>
    context.piblingSchedules.some((s) => s.id === event.payload.id && s.childNodes.length > 0);

const isLastUnselectedChild = (context: CopyVersionsMachineContext, event: SelectVersionToCopyEvent) =>
    context.piblingSchedules.find((p) => p.id === event.payload.parentId).childNodes.length -
        context.scheduleVersionIdsToCopy.filter((s) => s.parentId === event.payload.parentId).length ===
    1;

/*
    SERVICES
*/

const expandNode = async (_, event: ExpandOrCollapseEvent): Promise<NodeWithExpanded> => {
    const node = await getScheduleNode(event.payload.id, [NodeType.Schedule, NodeType.ScheduleVersion]);
    return createPiblingNode(node, true);
};

/*
    HELPERS
*/

const selectPiblingAndItsChildren = (context: CopyVersionsMachineContext, piblingId: string) => {
    const pibling = context.piblingSchedules.find((p) => p.id === piblingId);
    const childIds = pibling.childNodes.map((c) => ({ id: c.id, parentId: c.parentId, name: c.name }));
    return removeRepeatedItemsFromArray((v) => v.id, [...childIds, ...context.scheduleVersionIdsToCopy, { id: piblingId, parentId: null, name: pibling.name }]);
};
const removePiblingAndItsChildren = (context: CopyVersionsMachineContext, piblingId: string) => {
    const pibling = context.piblingSchedules.find((p) => p.id === piblingId);
    if (!pibling) {
        return context.scheduleVersionIdsToCopy.filter((v) => v.parentId !== piblingId);
    }
    return context.scheduleVersionIdsToCopy.filter((v) => v.parentId !== piblingId && v.id !== piblingId);
};

export const createPiblingNode = (node: Node, expand: boolean): NodeWithExpanded => ({ ...node, parentId: null, expandedInDialog: expand });

/*
    MACHINE
*/

export const copyScheduleVersionsMachine = createMachine<CopyVersionsMachineContext, CopyVersionsMachineEvents>({
    id: COPY_MACHINE_ID,
    context: {
        piblingSchedules: [],
        scheduleVersionIdsToCopy: [],
    },
    initial: "idle",
    states: {
        idle: {
            on: {
                EXPAND_OR_COLLAPSE: [
                    {
                        actions: assign({
                            piblingSchedules: (context, event) => context.piblingSchedules.map((s) => (s.id === event.payload.id ? { ...s, expandedInDialog: false } : s)),
                        }),
                        cond: isExpanded,
                    },
                    {
                        actions: assign({
                            piblingSchedules: (context, event) => context.piblingSchedules.map((s) => (s.id === event.payload.id ? { ...s, expandedInDialog: true } : s)),
                        }),
                        cond: isPreviouslyFetched,
                    },
                    { target: "expandingNode" },
                ],
                SELECT_OR_DESELECT_NODE: [
                    /* ORDER OF THESE CONDITIONS IS VERY IMPORTANT */
                    {
                        // Deselect pibling
                        actions: assign({
                            scheduleVersionIdsToCopy: (context, event) => removePiblingAndItsChildren(context, event.payload.id),
                        }),
                        cond: (c, e) => isPibling(c, e) && isSelected(c, e), // same logic whether expanded or collapsed
                    },
                    {
                        // Select pibling when pibling is expanded
                        actions: assign({
                            scheduleVersionIdsToCopy: (context, event) => selectPiblingAndItsChildren(context, event.payload.id),
                        }),
                        cond: (c, e) => isPibling(c, e) && isExpanded(c, e),
                    },
                    {
                        // Select pibling when pibling is collapsed and we do have the pibling's children in state
                        actions: assign({
                            piblingSchedules: (context, event) => context.piblingSchedules.map((p) => (p.id === event.payload.id ? { ...p, expandedInDialog: true } : p)),
                            scheduleVersionIdsToCopy: (context, event) => selectPiblingAndItsChildren(context, event.payload.id),
                        }),
                        cond: (c, e) => isPibling(c, e) && isPreviouslyFetched(c, e),
                    },
                    {
                        // Select pibling when pibling is collapsed and we don't have the pibling's children in state
                        target: "expandingNodeAndSelect",
                        cond: isPibling,
                    },
                    {
                        // Deselect child when all pibling and siblings are selected
                        actions: assign({
                            scheduleVersionIdsToCopy: (context, event) =>
                                context.scheduleVersionIdsToCopy.filter((s) => s.id !== event.payload.id && s.id !== event.payload.parentId),
                        }),
                        cond: (c, e) => isSelected(c, e) && isPiblingSelected(c, e),
                    },
                    {
                        // Deselect child
                        actions: assign({
                            scheduleVersionIdsToCopy: (context, event) => context.scheduleVersionIdsToCopy.filter((s) => s.id !== event.payload.id),
                        }),
                        cond: isSelected,
                    },
                    {
                        // Select child when child is last unselected child of pibling
                        actions: assign({
                            scheduleVersionIdsToCopy: (context, event) => [
                                ...context.scheduleVersionIdsToCopy,
                                event.payload,
                                { id: event.payload.parentId, parentId: null, name: event.payload.name }, // Don't forget the pibling!
                            ],
                        }),
                        cond: isLastUnselectedChild,
                    },
                    {
                        // Select child
                        actions: assign({ scheduleVersionIdsToCopy: (context, event) => [...context.scheduleVersionIdsToCopy, event.payload] }),
                    },
                ],
            },
        },
        expandingNodeAndSelect: {
            invoke: {
                src: expandNode,
                onDone: {
                    actions: assign({
                        piblingSchedules: (context, event) => context.piblingSchedules.map((s) => (s.id === event.data.id ? createPiblingNode(event.data, true) : s)),
                        scheduleVersionIdsToCopy: (context, event: DoneInvokeEvent<NodeWithExpanded>) => selectPiblingAndItsChildren(context, event.data.id),
                    }),
                    target: "idle",
                },
                onError: "failure",
            },
        },
        expandingNode: {
            invoke: {
                src: expandNode,
                onDone: {
                    actions: assign({
                        piblingSchedules: (context, event) => context.piblingSchedules.map((s) => (s.id === event.data.id ? event.data : s)),
                    }),
                    target: "idle",
                },
                onError: "failure",
            },
        },
        failure: {},
    },
});
